import Image from 'next/image';
import React from 'react';
import { PayoutTermDTO, PayoutTermType } from 'src/modules/conversions/infra/dtos';
import { useCurrency } from 'src/modules/currencies';
import { CurrencyIcon } from 'src/modules/currencies/components/CurrencyIcon';
import { formatUnits } from 'viem';

import { Skeleton, Text } from '@/design-system/components/ui';
import { formatDecimalAmount, maskAddressValue } from '@/utils/format';

interface Props {
  payoutTerm: PayoutTermDTO;
}

export const PayoutCaps = ({ payoutTerm }: Props) => {
  const {
    type,
    payout_currency_address: payoutCurrencyAddress,
    payout_currency_chain_id: payoutCurrencyChainId,
    wallet_cap_amount: walletCapAmount,
    wallet_cap_window_days: walletCapWindowDays,
    end_user_cap_amount: endUserCapAmount,
    end_user_cap_window_days: endUserCapWindowDays,
    max_payout_amount: maxPayoutAmount,
    dynamic_referral_cap_multiplier: dynamicReferralCapMultiplier,
    payout_groups: payoutGroups,
  } = payoutTerm ?? {};

  // For tiered incentives, referral cap is only active when at least one payout group has it enabled
  const isTiered = (payoutGroups?.length ?? 0) > 0;
  const hasDynamicReferralCap =
    dynamicReferralCapMultiplier != null &&
    dynamicReferralCapMultiplier > 0 &&
    (!isTiered || payoutGroups?.some((pg) => pg.dynamic_referral_cap_enabled));

  const isPoints = type === PayoutTermType.Point;
  const getPointLabel = (amount: string | null | undefined) => {
    return amount === '1' ? 'POINT' : 'POINTS';
  };

  const { data: currency, isLoading: isCurrencyLoading } = useCurrency(
    isPoints ? undefined : payoutCurrencyAddress,
    isPoints ? undefined : payoutCurrencyChainId?.toString(),
  );

  const currencySymbol = isPoints ? 'POINTS' : (currency?.name ?? maskAddressValue(payoutCurrencyAddress));
  const payoutCurrencyDecimals = isPoints ? 0 : (currency?.decimals ?? 18);

  return (
    <div className="tw:flex tw:flex-col tw:gap-[0.8rem] tw:py-[0.2rem] tw:border-l-[0.2rem] tw:border-l-[var(--mui-palette-primary-main)] tw:pl-2">
      {maxPayoutAmount && (
        <div className="tw:flex tw:gap-8 tw:items-center">
          <Text variant="body-md" color="muted" className="tw:w-[145px]">
            Max payout amount
          </Text>

          {isCurrencyLoading && !isPoints ? (
            <Skeleton width={120} height={20} />
          ) : (
            <div className="tw:flex tw:items-center tw:gap-[0.3rem]">
              <div className="tw:w-5 tw:h-5 tw:relative">
                {isPoints ? (
                  <Image src="/assets/svg/coin.svg" alt={payoutCurrencyAddress} fill />
                ) : (
                  <CurrencyIcon logoUri={currency?.logoUri} name={currencySymbol} size={20} />
                )}
              </div>
              <Text variant="body-md">
                {formatDecimalAmount(
                  Number(isPoints ? maxPayoutAmount : formatUnits(BigInt(maxPayoutAmount), payoutCurrencyDecimals)),
                )}
              </Text>
              <Text variant="body-md">{isPoints ? getPointLabel(maxPayoutAmount) : currencySymbol}</Text>
              <Text variant="body-md">per transaction</Text>
            </div>
          )}
        </div>
      )}
      {/*
      TODO:
      - El wallet cap y end user cap es el mismo codigo se podria crear 1 componente para meter la logica ahi y solo recibir los montos.
      */}
      {walletCapAmount && (
        <div className="tw:flex tw:gap-8 tw:items-center">
          <Text variant="body-md" color="muted" className="tw:w-[145px]">
            Wallet cap amount
          </Text>

          {isCurrencyLoading && !isPoints ? (
            <Skeleton width={120} height={20} />
          ) : (
            <div className="tw:flex tw:items-center tw:gap-[0.3rem]">
              <div className="tw:w-5 tw:h-5 tw:relative">
                {isPoints ? (
                  <Image src="/assets/svg/coin.svg" alt={payoutCurrencyAddress} fill />
                ) : (
                  <CurrencyIcon logoUri={currency?.logoUri} name={currencySymbol} size={20} />
                )}
              </div>
              <Text variant="body-md">
                {formatDecimalAmount(
                  Number(isPoints ? walletCapAmount : formatUnits(BigInt(walletCapAmount), payoutCurrencyDecimals)),
                )}{' '}
                {isPoints ? getPointLabel(walletCapAmount) : currencySymbol}{' '}
                {walletCapWindowDays && walletCapWindowDays !== 10000
                  ? `every ${walletCapWindowDays} days`
                  : 'per wallet'}
              </Text>
            </div>
          )}
        </div>
      )}
      {hasDynamicReferralCap && (
        <div className="tw:flex tw:gap-8 tw:items-center">
          <Text variant="body-md" color="muted" className="tw:w-[145px]">
            Referral cap
          </Text>
          <Text variant="body-md">{dynamicReferralCapMultiplier}x of earnings as end user</Text>
        </div>
      )}
      {endUserCapAmount && (
        <div className="tw:flex tw:gap-8 tw:items-center">
          <Text variant="body-md" color="muted" className="tw:w-[145px]">
            End user cap amount
          </Text>

          {isCurrencyLoading && !isPoints ? (
            <Skeleton width={120} height={20} />
          ) : (
            <div className="tw:flex tw:items-center tw:gap-[0.3rem]">
              <div className="tw:w-5 tw:h-5 tw:relative">
                {isPoints ? (
                  <Image src="/assets/svg/coin.svg" alt={payoutCurrencyAddress} fill />
                ) : (
                  <CurrencyIcon logoUri={currency?.logoUri} name={currencySymbol} size={20} />
                )}
              </div>
              <Text variant="body-md">
                {formatDecimalAmount(
                  Number(isPoints ? endUserCapAmount : formatUnits(BigInt(endUserCapAmount), payoutCurrencyDecimals)),
                )}{' '}
                {isPoints ? getPointLabel(endUserCapAmount) : currencySymbol}{' '}
                {endUserCapWindowDays && endUserCapWindowDays !== 10000
                  ? `every ${endUserCapWindowDays} days`
                  : 'per transacting user'}
              </Text>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
